import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import FindReplaceRoundedIcon from '@mui/icons-material/FindReplaceRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

export const PROMPT_EXAMPLES = [
  {
    id: 'name_mobile',
    title: 'Ask Name & Mobile Number (Lead Capture)',
    tag: 'Lead Capture',
    description: 'Ask customer for name and mobile number, save in variables, and confirm.',
    text: `You are a polite and professional customer assistant.
1. Greet the customer warmly.
2. Ask for the customer's full name and store it in {variables.name}.
3. Ask for their mobile number and store it in {variables.mobile}.
4. Confirm their details once provided and thank them.`,
  },
  {
    id: 'customer_support',
    title: 'Customer Support & Live Handoff',
    tag: 'Support',
    description: 'Provide helpful assistance and transfer to live agent when needed.',
    text: `You are a helpful customer support agent for our company.
1. Understand the customer's request using the conversation context {session.lastInboundMessage}.
2. Provide clear, accurate, and concise answers.
3. If the user asks for a human agent or has an unresolved issue, transfer them to live support.`,
  },
  {
    id: 'appointment_booking',
    title: 'Appointment & Service Booking',
    tag: 'Booking',
    description: 'Collect service choice, appointment date, and contact number.',
    text: `You are an appointment booking coordinator.
1. Ask the customer what service they need and store it in {variables.service}.
2. Ask for their preferred appointment date and store it in {variables.date}.
3. Ask for their phone number {variables.mobile} to confirm the booking.
4. Summarize the appointment details before completing.`,
  },
  {
    id: 'feedback_survey',
    title: 'Customer Feedback & Rating',
    tag: 'Survey',
    description: 'Collect rating (1-5) and feedback comments.',
    text: `Please ask the customer to rate their experience today on a scale of 1 to 5.
Store their rating in {variables.rating}.
Ask if they have any additional feedback or comments and save in {variables.feedback}.
Thank the customer for their valuable time.`,
  },
];

/** Purpose: edit a reusable AI prompt in a focused modal. Why: longer prompt content benefits from a larger editor than the compact node panel. */
export default function PromptEditorModal({
  isOpen,
  onClose,
  value = '',
  onChange,
  title = 'Prompt Editor',
  subtitle = 'Full-screen editor with variable autocomplete and starter templates',
  variables = [],
}) {
  const [draftValue, setDraftValue] = useState(value);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('editor'); // 'editor' | 'examples'
  const [templateQuery, setTemplateQuery] = useState(null);
  const [variableSearch, setVariableSearch] = useState('');
  const [variableMenuOpen, setVariableMenuOpen] = useState(false);
  const textareaRef = useRef(null);
  const variableMenuRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDraftValue(value || '');
      setTemplateQuery(null);
      setVariableSearch('');
      setVariableMenuOpen(false);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 80);
    }
  }, [isOpen, value]);

  // Lock body scroll when full-screen editor is open
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Handle ESC and Ctrl/Cmd+Enter keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;
    /** Purpose: Handle suggestion dismissal, editor dismissal, and apply shortcuts. Why: Escape must close the variable menu before closing the editor. */
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && variableMenuOpen) {
        e.preventDefault();
        e.stopPropagation();
        setVariableMenuOpen(false);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, draftValue, variableMenuOpen]);

  useEffect(() => {
    if (!variableMenuOpen) return undefined;
    /** Purpose: Close suggestions on an outside pointer event. Why: The picker should not obscure editing after focus moves. */
    const handlePointerDown = (event) => {
      if (!variableMenuRef.current?.contains(event.target)) setVariableMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [variableMenuOpen]);

  // Extract variables list
  const normalizedVariables = Array.isArray(variables) ? variables : [];
  /** Purpose: Merge configured and built-in variable choices. Why: Prompt authors need one deduplicated insertion catalog. */
  const variableOptions = useMemo(() => {
    const seen = new Set();
    const result = [];

    // Common standard variables
    const defaults = [
      { value: 'variables.name', label: 'variables.name', detail: 'Contact Name' },
      { value: 'variables.mobile', label: 'variables.mobile', detail: 'Mobile Number' },
      { value: 'variables.email', label: 'variables.email', detail: 'Email Address' },
      { value: 'journey.name', label: 'journey.name', detail: 'Journey Name' },
      { value: 'journey.mobile', label: 'journey.mobile', detail: 'Journey Mobile' },
      { value: 'session.language', label: 'session.language', detail: 'Conversation Language' },
      { value: 'session.lastInboundMessage', label: 'session.lastInboundMessage', detail: 'Last Message' },
    ];

    defaults.forEach((opt) => {
      seen.add(opt.value);
      result.push(opt);
    });

    normalizedVariables.forEach((variable) => {
      const name = String(variable?.name || '').trim();
      const scope = String(variable?.scope || 'journey').toLowerCase();
      if (!name) return;
      const ref1 = `${scope}.${name}`;
      const ref2 = `variables.${name}`;
      if (!seen.has(ref1)) {
        seen.add(ref1);
        result.push({ value: ref1, label: ref1, detail: `${scope} variable` });
      }
      if (!seen.has(ref2)) {
        seen.add(ref2);
        result.push({ value: ref2, label: ref2, detail: 'variable' });
      }
    });

    return result;
  }, [normalizedVariables]);

  /** Purpose: Search variable labels and descriptions. Why: Large catalogs must remain usable without exhaustive scrolling. */
  const filteredVariables = useMemo(() => {
    if (!variableSearch) return variableOptions;
    const q = variableSearch.toLowerCase();
    return variableOptions.filter((opt) => opt.label.toLowerCase().includes(q) || opt.detail.toLowerCase().includes(q));
  }, [variableOptions, variableSearch]);

  // Insert variable at cursor
  /** Purpose: Resolve insert variable in the prompt editor modal module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
  const insertVariable = (reference) => {
    const textarea = textareaRef.current;
    const current = String(draftValue || '');
    const cursor = textarea?.selectionStart ?? current.length;
    const match = current.slice(0, cursor).match(/\{{1,3}\s*[a-zA-Z0-9_.-]*$/);
    const start = match ? cursor - match[0].length : cursor;
    const end = textarea?.selectionEnd ?? cursor;
    const formatted = `{${reference}}`;
    const next = `${current.slice(0, start)}${formatted}${current.slice(end)}`;
    setDraftValue(next);
    setTemplateQuery(null);
    setVariableMenuOpen(false);
    setVariableSearch('');
    window.requestAnimationFrame(() => {
      const nextPos = start + formatted.length;
      textarea?.focus();
      textarea?.setSelectionRange(nextPos, nextPos);
    });
  };

  // Insert example text
  /** Purpose: Handle handle insert example in the prompt editor modal module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleInsertExample = (exampleText, replace = false) => {
    const textarea = textareaRef.current;
    if (replace || !draftValue.trim()) {
      setDraftValue(exampleText);
    } else {
      const current = String(draftValue || '');
      const cursor = textarea?.selectionStart ?? current.length;
      const next = `${current.slice(0, cursor)}\n\n${exampleText}\n\n${current.slice(cursor)}`;
      setDraftValue(next);
    }
    setActiveTab('editor');
    window.requestAnimationFrame(() => {
      textarea?.focus();
    });
  };

  /** Purpose: Detect an unfinished template before the cursor. Why: Autocomplete must follow the current insertion point. */
  const updateSuggestion = (rawValue, cursor) => {
    const beforeCursor = rawValue.slice(0, cursor);
    const match = beforeCursor.match(/\{{1,3}\s*([a-zA-Z0-9_.-]*)$/);
    setTemplateQuery(match ? match[1] : null);
  };

  /** Purpose: Apply the draft and close the editor. Why: The parent receives the prompt through the explicit apply action. */
  const handleApply = () => {
    onChange(draftValue);
    onClose();
  };

  /** Purpose: Copy the prompt and briefly show confirmation. Why: Authors need to reuse text without changing their draft. */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draftValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  // Live stats
  const charCount = draftValue.length;
  const wordCount = draftValue.trim() ? draftValue.trim().split(/\s+/).length : 0;
  /** Purpose: Extract unique variable templates from the draft. Why: The editor can expose which references the prompt depends on. */
  const detectedVariables = useMemo(() => {
    const matches = draftValue.match(/(?:\{\{\{|\{\{|\{)\s*([a-zA-Z0-9_.-]+)\s*(?:\}\}\}|\}\}|\})/g) || [];
    return [...new Set(matches.map((m) => m.replace(/[\{\}\s]/g, '')))];
  }, [draftValue]);

  const matchingAutocomplete = templateQuery === null
    ? []
    : variableOptions.filter((opt) => opt.label.toLowerCase().includes(templateQuery.toLowerCase())).slice(0, 8);

  if (!isOpen) return null;

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[999999] w-screen h-screen flex flex-col bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 overflow-hidden animate-fadeIn"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 sm:px-8 py-3.5 bg-gradient-to-r from-slate-50 via-white to-cyan-50/40 dark:border-white/10 dark:from-slate-900 dark:via-slate-900 dark:to-cyan-950/20 shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-cyan-500 to-violet-500 text-white shadow-md shadow-cyan-500/20">
            <AutoAwesomeRoundedIcon fontSize="small" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">{title}</h3>
              <span className="rounded-full bg-cyan-100 dark:bg-cyan-950/60 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-500/30">
                Full Screen
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
          </div>
        </div>

        {/* Stats & Actions */}
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white/80 px-3.5 py-1.5 text-xs text-slate-500 dark:border-white/10 dark:bg-slate-800/80 dark:text-slate-400">
            <span><strong>{charCount}</strong> chars</span>
            <span className="text-slate-300 dark:text-slate-700">•</span>
            <span><strong>{wordCount}</strong> words</span>
            {detectedVariables.length > 0 && (
              <>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <span className="text-cyan-600 dark:text-cyan-400 font-medium">
                  {detectedVariables.length} variable{detectedVariables.length > 1 ? 's' : ''}
                </span>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            title="Copy prompt text"
          >
            {copied ? <CheckRoundedIcon fontSize="small" className="text-emerald-500" /> : <ContentCopyRoundedIcon fontSize="small" />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>

          <button
            type="button"
            onClick={handleApply}
            className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-md shadow-cyan-500/20 transition hover:from-cyan-500 hover:to-blue-500"
          >
            <CheckRoundedIcon fontSize="small" />
            <span>Apply & Save</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 transition"
            title="Exit Fullscreen (Esc)"
          >
            <FullscreenExitRoundedIcon fontSize="small" />
            <span>Exit Fullscreen</span>
          </button>
        </div>
      </div>

        {/* Toolbar & Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/80 px-5 sm:px-8 py-2.5 dark:border-white/10 dark:bg-slate-900/60 shrink-0">
          {/* Tabs */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setActiveTab('editor')}
              className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
                activeTab === 'editor'
                  ? 'bg-white shadow-sm text-cyan-700 dark:bg-slate-800 dark:text-cyan-300'
                  : 'text-slate-600 hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-slate-800/60'
              }`}
            >
              Editor
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('examples')}
              className={`inline-flex items-center gap-1 rounded-lg px-3 py-1 text-xs font-semibold transition ${
                activeTab === 'examples'
                  ? 'bg-white shadow-sm text-violet-700 dark:bg-slate-800 dark:text-violet-300'
                  : 'text-slate-600 hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-slate-800/60'
              }`}
            >
              <AutoAwesomeRoundedIcon className="h-3.5 w-3.5 text-violet-500" />
              <span>Prompt Examples ({PROMPT_EXAMPLES.length})</span>
            </button>
          </div>

          {/* Quick Variable Pills */}
          <div className="flex max-w-full flex-wrap items-center gap-1.5 py-0.5">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">
              Insert:
            </span>
            {['variables.name', 'variables.mobile', 'variables.email', 'journey.name', 'session.language'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => insertVariable(v)}
                className="shrink-0 rounded-lg border border-cyan-200 bg-cyan-50/60 px-2.5 py-1 font-mono text-[11px] font-medium text-cyan-700 transition hover:bg-cyan-100 hover:border-cyan-300 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-300 dark:hover:bg-cyan-500/20"
                title={`Insert {${v}} at cursor`}
              >
                {`{${v}}`}
              </button>
            ))}

            <div className="relative shrink-0" ref={variableMenuRef}>
              <button
                type="button"
                aria-label="More variables"
                aria-haspopup="listbox"
                aria-expanded={variableMenuOpen}
                onClick={() => {
                  setVariableMenuOpen((open) => !open);
                  setVariableSearch('');
                }}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-800 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10"
              >
                {variableMenuOpen ? 'Close variables' : '+ All Variables'}
                <span aria-hidden="true" className="text-[10px]">⌄</span>
              </button>
              {variableMenuOpen ? <div className="absolute right-0 top-full z-[100] mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.22)] dark:border-white/10 dark:bg-slate-900">
                <div className="border-b border-slate-100 p-2.5 dark:border-white/10">
                  <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 dark:border-white/10 dark:bg-slate-950">
                    <SearchRoundedIcon className="h-4 w-4 shrink-0 text-slate-400" />
                    <input
                      autoFocus
                      type="search"
                      aria-label="Search variables"
                      value={variableSearch}
                      onChange={(event) => setVariableSearch(event.target.value)}
                      placeholder="Search variables..."
                      className="min-w-0 flex-1 bg-transparent py-2 text-xs text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100"
                    />
                  </label>
                </div>
                <div role="listbox" aria-label="Available variables" className="max-h-[min(55vh,28rem)] overflow-y-auto p-1.5">
                  {['variables', 'journey', 'session', 'global', 'system'].map((scope) => {
                    /** Purpose: Select variable options belonging to a scope. Why: Grouped suggestions must preserve namespace boundaries. */
                    const scoped = filteredVariables.filter((option) => option.value.startsWith(`${scope}.`));
                    if (!scoped.length) return null;
                    return <div key={scope} className="py-1">
                      <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{scope}</div>
                      {scoped.map((option) => <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => insertVariable(option.value)}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left transition hover:bg-cyan-50 focus:bg-cyan-50 focus:outline-none dark:hover:bg-cyan-500/10 dark:focus:bg-cyan-500/10"
                      >
                        <span className="truncate font-mono text-xs font-medium text-slate-700 dark:text-slate-200">{option.label}</span>
                        <span className="shrink-0 text-[10px] text-slate-400">{option.detail}</span>
                      </button>)}
                    </div>;
                  })}
                  {filteredVariables.length === 0 ? <div className="px-3 py-8 text-center text-xs text-slate-500">No matching variables found.</div> : null}
                </div>
                <div className="border-t border-slate-100 px-3 py-2 text-[10px] text-slate-400 dark:border-white/10">Select a variable to insert it at the cursor.</div>
              </div> : null}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        {activeTab === 'examples' ? (
          <div className="flex-1 overflow-y-auto p-5 sm:p-6 bg-slate-50/50 dark:bg-slate-950/30">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white">Ready-to-Use Prompt Templates</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Click any template to insert it into your prompt editor. Variables like {`{variables.name}`} will be filled automatically during chat.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('editor')}
                className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
              >
                Back to Editor
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PROMPT_EXAMPLES.map((example) => (
                <div
                  key={example.id}
                  className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md hover:border-cyan-200 dark:border-white/10 dark:bg-slate-900 dark:hover:border-cyan-500/30"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h5 className="font-semibold text-sm text-slate-900 dark:text-white">{example.title}</h5>
                      <span className="rounded-full bg-violet-100 dark:bg-violet-950/60 px-2 py-0.5 text-[10px] font-bold text-violet-700 dark:text-violet-300">
                        {example.tag}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{example.description}</p>
                    <pre className="rounded-xl bg-slate-50 dark:bg-slate-950 p-3 text-xs font-mono text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto border border-slate-100 dark:border-white/5">
                      {example.text}
                    </pre>
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-white/5">
                    <button
                      type="button"
                      onClick={() => handleInsertExample(example.text, false)}
                      className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <AddCircleOutlineRoundedIcon fontSize="small" />
                      <span>Append</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInsertExample(example.text, true)}
                      className="inline-flex items-center gap-1 rounded-xl bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500"
                    >
                      <FindReplaceRoundedIcon fontSize="small" />
                      <span>Use this template</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0 relative bg-white dark:bg-slate-900">
            <textarea
              ref={textareaRef}
              value={draftValue}
              onChange={(e) => {
                setDraftValue(e.target.value);
                updateSuggestion(e.target.value, e.target.selectionStart ?? e.target.value.length);
              }}
              onClick={(e) => updateSuggestion(e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length)}
              onKeyUp={(e) => updateSuggestion(e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length)}
              placeholder="Write your prompt instructions here... e.g.&#10;Please ask customer for their name and store in {variables.name}, then ask for their mobile number and store in {variables.mobile}."
              className="w-full flex-1 p-5 sm:p-7 font-mono text-sm sm:text-base leading-relaxed bg-transparent border-0 focus:ring-0 resize-none outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
            />

            {/* Variable Autocomplete popup */}
            {templateQuery !== null && (
              <div className="absolute left-6 bottom-16 z-30 max-h-56 w-80 overflow-y-auto rounded-xl border border-cyan-200 bg-white shadow-2xl dark:border-cyan-400/30 dark:bg-slate-950">
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-white/5">
                  Insert Variable
                </div>
                {matchingAutocomplete.length > 0 ? (
                  matchingAutocomplete.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertVariable(option.value)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs text-slate-800 transition hover:bg-cyan-50 dark:text-slate-100 dark:hover:bg-cyan-500/15"
                    >
                      <code className="text-cyan-700 dark:text-cyan-300 font-semibold">{`{${option.label}}`}</code>
                      <span className="text-[10px] text-slate-500">{option.detail}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                    No variables match "{templateQuery}".
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer info bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 sm:px-8 py-3 text-xs text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-400 shrink-0">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-cyan-500 animate-pulse" />
            <span>
              <strong>Tip:</strong> Type <code className="font-mono text-cyan-700 dark:text-cyan-300 font-semibold">{'{variables.name}'}</code> or <code className="font-mono text-cyan-700 dark:text-cyan-300 font-semibold">{'{{journey.name}}'}</code> to inject or collect variables during chat.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-slate-400">Esc to exit • Ctrl+Enter to apply</span>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-3.5 py-1.5 font-medium text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Exit
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-1.5 font-semibold text-white shadow-md shadow-cyan-500/20 hover:from-cyan-500 hover:to-blue-500 transition"
            >
              <CheckRoundedIcon fontSize="small" />
              <span>Apply Changes</span>
            </button>
          </div>
        </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
}
